import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { X, Quote, AlertTriangle, CheckCircle2, HelpCircle } from 'lucide-react';

const VERDICT = {
  supported: { label: 'Supported by your library', cls: 'border-emerald-500 text-emerald-700 dark:text-emerald-400', Icon: CheckCircle2 },
  partial: { label: 'Weak / indirect support', cls: 'border-amber-500 text-amber-700 dark:text-amber-400', Icon: HelpCircle },
  unsupported: { label: 'No support found', cls: 'border-rose-500 text-rose-700 dark:text-rose-400', Icon: AlertTriangle },
};
const CITATION = {
  mismatch: 'The cited source does not say this',
  not_in_library: 'The cited source is not in your library — add its PDF to verify',
  matches: 'The cited source supports this',
  none: 'No citation in this sentence',
};

export default function ClaimConfidenceCard({ claim, onClose, onInsertCitation, onDismiss }) {
  const v = VERDICT[claim.verdict] || VERDICT.partial;
  return (
    <div className="rounded-lg border border-border bg-card p-3 space-y-2" data-testid="claim-confidence-card">
      <div className="flex items-start justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="outline" className={`text-[10px] ${v.cls}`} data-testid="claim-verdict-badge">
            <v.Icon className="w-3 h-3 mr-1" /> {v.label}
          </Badge>
          <span className="text-[10px] text-muted-foreground">confidence {Math.round((claim.confidence || 0) * 100)}%</span>
          {claim.citation_status !== 'none' && (
            <Badge variant="outline"
              className={`text-[10px] ${claim.citation_status === 'mismatch' ? 'border-rose-500 text-rose-700 dark:text-rose-400' : ''}`}
              data-testid="claim-citation-badge">
              {CITATION[claim.citation_status]}
            </Badge>
          )}
        </div>
        <button onClick={onClose} className="text-muted-foreground hover:text-foreground"
          data-testid="claim-card-close"><X className="w-4 h-4" /></button>
      </div>

      <p className="text-[12px] italic text-muted-foreground line-clamp-2">“{claim.text}”</p>
      {claim.rationale && <p className="text-[12px]" data-testid="claim-rationale">{claim.rationale}</p>}
      {claim.suggestion && (
        <p className="text-[12px] text-[#1d4ed8] dark:text-[#7aa9ff]" data-testid="claim-suggestion">
          Fix: {claim.suggestion}
        </p>
      )}

      {!!claim.evidence?.length && (
        <div className="space-y-1.5" data-testid="claim-evidence-list">
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Evidence from your library</div>
          {claim.evidence.slice(0, 4).map((e) => (
            <div key={e.eid} className="p-2 rounded border border-border/70 space-y-1">
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="text-[11px] font-medium">{e.title}</span>
                {e.from_cited_source && <Badge variant="outline" className="text-[9px]">cited source</Badge>}
                {e.doi && <span className="text-[10px] text-muted-foreground">doi:{e.doi}</span>}
              </div>
              <p className="text-[11px] text-muted-foreground">{e.excerpt}</p>
              {e.token && (
                <Button size="sm" variant="outline" className="h-6 px-2 text-[10px]"
                  onClick={() => onInsertCitation(e.token)}
                  data-testid={`claim-insert-citation-${e.eid}`}>
                  <Quote className="w-3 h-3 mr-1" /> Cite this here
                </Button>
              )}
            </div>
          ))}
        </div>
      )}

      <Button size="sm" variant="ghost" className="h-6 px-2 text-[10px]" onClick={onDismiss}
        data-testid="claim-dismiss-btn">Dismiss this marking</Button>
    </div>
  );
}
